import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MeasurementsDialog from "./MeasurementsDialog";
import dummyProfile from "../../assets/dummyprofile.png";
import { CircularProgress } from "@mui/material";

/* ------------------------------------------------------------------ */
/* HELPERS */
/* ------------------------------------------------------------------ */

const safeText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (Array.isArray(value)) return value.map(safeText).join(", ");
  return "";
};

const alternatingRowStyle = (index) => ({
  backgroundColor: index % 2 === 0 ? "#eee" : "#ffffff",
});

/* ------------------------------------------------------------------ */
/* Editable Placeholder */
/* ------------------------------------------------------------------ */

const EditablePlaceholder = ({
  value,
  placeholder,
  editMode,
  onEdit,
  textSx = {},
}) => {
  const displayValue = safeText(value);

  return (
    <Box
      onClick={editMode ? onEdit : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        cursor: editMode ? "pointer" : "default",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          ...(displayValue ? {} : { fontStyle: "italic", color: "#bbb" }),
          ...textSx,
        }}
      >
        {displayValue || placeholder}
      </Typography>

      {editMode && <EditIcon fontSize="small" sx={{ color: "#bbb" }} />}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* COMPONENT */
/* ------------------------------------------------------------------ */

const TalentSidebar = ({
  profilePic,
  talentData = {},
  affiliations = [],
  vocalTypes = [],
  vocalRange = [],
  sidebarMediaEditMode,
  setEditPromptOpen,
  setEditSection,
  setEditInitialData,
  updateDocuments,
}) => {
  const [measureOpen, setMeasureOpen] = useState(false);

  /* ---------------- MEASUREMENTS ---------------- */

  const rawMeasurements = [
    {
      label: "Height",
      value:
        talentData.heightStart != null && talentData.heightEnd != null
          ? `${talentData.heightStart}' ${talentData.heightEnd}"`
          : talentData.heightStart != null
            ? `${talentData.heightStart}'`
            : null,
    },
    {
      label: "Weight",
      value: talentData.weight ? `${talentData.weight} lbs` : null,
    },
    { label: "Eye Color", value: talentData.eyeColor || null },
    { label: "Hair Color", value: talentData.hairColor || null },
    { label: "Dress (US)", value: talentData.dress || null },
    { label: "Bust (in)", value: talentData.bust || null },
    { label: "Waist (W)", value: talentData.waist || null },
    { label: "Hips (in)", value: talentData.hips || null },
    { label: "Shoe (W)", value: talentData.shoe || null },
    { label: "Suit (Chest)", value: talentData.maleSuit || null },
    { label: "Shirt", value: talentData.maleShirt || null },
    { label: "Waist (in)", value: talentData.maleWaist || null },
    { label: "Inseam (in)", value: talentData.maleInseam || null },
    { label: "Shoe (M)", value: talentData.maleShoe || null },
  ];

  const measurements = rawMeasurements.filter((m) => m.value != null);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const hasVocalData =
    !!talentData.vocalRangeID ||
    (Array.isArray(vocalTypes) && vocalTypes.length > 0);

  const hasDocumentsData =
    !!talentData.hasPassport || !!talentData.hasDriverLicencse;

  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
    setImageLoaded(false);
  }, [profilePic]);

  /* ------------------------------------------------------------------ */
  /* RENDER */
  /* ------------------------------------------------------------------ */

  return (
    <>
      <Box
        pl={1}
        pt={1}
        pb={1}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.55,
          cursor: sidebarMediaEditMode ? "pointer" : "default",
          whiteSpace: "normal",
          flexWrap: "wrap",
        }}
        onClick={() => {
          if (!sidebarMediaEditMode) return;
          setEditSection("Name");
          setEditInitialData({
            firstName: talentData.firstName || "",
            middleName: talentData.middleName || "",
            lastName: talentData.lastName || "",
          });
          setEditPromptOpen(true);
        }}
      >
      <Typography
  variant="caption"
  sx={{
    fontSize: "18px",
    textTransform: "uppercase",
    lineHeight: 1,
    fontWeight: 700,
  }}
>
  {`${talentData.firstName || ""} ${talentData.middleName || ""} ${
    talentData.lastName || ""
  }` || "Add your name"}
</Typography>

        {/* Reserve space even when hidden */}
        {sidebarMediaEditMode && (
  <EditIcon
    fontSize="small"
    sx={{ color: "#bbb", position: "relative", top: "-5px" }}

  />
)}
      </Box>

      <Box
        sx={{
          backgroundColor: "#fff",
          padding: "8px",
          width: "100%",
          maxWidth: { xs: "100%", md: 250 },
          borderRadius: "4px",
          marginTop: "9px",
        }}
      >
        {/* PROFILE PIC */}
        <Box
          sx={{
            position: "relative",
            cursor: sidebarMediaEditMode ? "pointer" : "default",
            backgroundColor: "#f5f5f5",
          }}
        >
          {/* Loader */}
          {imageLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={30} />
            </Box>
          )}

          {/* Actual Image */}
          {profilePic && !imageError && (
            <img
              src={profilePic}
              alt="Profile"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",

                filter: imageLoaded ? "blur(0px)" : "blur(12px)",
                transform: imageLoaded ? "scale(1)" : "scale(1.05)",
                opacity: imageLoaded ? 1 : 0.7,
                transition: "all 0.6s ease",

                display: imageLoading ? "none" : "block",
              }}
              onLoad={() => {
                setImageLoading(false);
                setTimeout(() => setImageLoaded(true), 50);
              }}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
            />
          )}

          {/* Fallback */}
          {!imageLoading && (imageError || !profilePic) && (
            <img
              src={dummyProfile}
              alt="Dummy Profile"
              style={{
                width: "100%",
                objectFit: "cover",
                border: "1px solid #eee",
              }}
            />
          )}

          {/* Edit Button */}
          {sidebarMediaEditMode && (
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "#fff",
              }}
              onClick={() => {
                setEditSection("Profile Picture");
                setEditInitialData({});
                setEditPromptOpen(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* AFFILIATIONS */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            padding: "4px",
            backgroundColor: "#202020",
            color: "#eee",
            cursor: sidebarMediaEditMode ? "pointer" : "default",
            whiteSpace: "nowrap",
          }}
          onClick={() => {
            if (!sidebarMediaEditMode) return;
            setEditSection("Affiliations");
            setEditInitialData({
              affiliationIds: affiliations.map((a) => a.affiliationId),
            });
            setEditPromptOpen(true);
          }}
        >
          <Typography sx={{ fontSize: "15px" }} noWrap>
            {safeText(affiliations.map((a) => a.affiliationName)) ||
              "Add union affiliations"}
          </Typography>

          {sidebarMediaEditMode && (
            <EditIcon fontSize="small" sx={{ color: "#bbb" }} />
          )}
        </Box>

        {/* MEASUREMENTS */}
        <Box
          sx={{
            visibility:
              measurements.length > 0 || sidebarMediaEditMode
                ? "visible"
                : "hidden",
            height:
              measurements.length > 0 || sidebarMediaEditMode ? "auto" : 0,
            overflow: "hidden",
          }}
        >
          <Box mt={2}>
            <Box display="flex" alignItems="center">
              <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Measurements
              </Typography>
              {sidebarMediaEditMode && (
                <IconButton size="small" onClick={() => setMeasureOpen(true)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {measurements.length > 0 && (
              <Box>
                {measurements.map(({ label, value }, i) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 1,
                      py: 0.7,
                      backgroundColor: i % 2 === 0 ? "#eee" : "#fff",
                      fontSize: "14px",
                    }}
                  >
                    <Box sx={{ color: "#555" }}>{label}</Box>
                    <Box sx={{ fontWeight: 500 }}>{value}</Box>
                  </Box>
                ))}
              </Box>
            )}

            <MeasurementsDialog
              open={measureOpen}
              onClose={() => setMeasureOpen(false)}
              talentData={talentData}
              initialData={talentData}
              onSave={(values) => updateDocuments(values)}
            />
          </Box>
        </Box>

        {/* VOCAL */}
        {(hasVocalData || sidebarMediaEditMode) && (
          <Box mt={2}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
              Vocal
            </Typography>

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>
                    <EditablePlaceholder
                      value={vocalTypes}
                      placeholder="Add vocal styles"
                      editMode={sidebarMediaEditMode}
                      onEdit={() => {
                        setEditSection("Vocal Type");
                        setEditPromptOpen(true);
                      }}
                      textSx={{ fontSize: "15px" }}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Range</TableCell>
                  <TableCell>
                    <EditablePlaceholder
                      value={
                        vocalRange.find(
                          (r) => r.vocalRangeId === talentData.vocalRangeID,
                        )?.vocalRangeName
                      }
                      placeholder="Add vocal range"
                      editMode={sidebarMediaEditMode}
                      onEdit={() => {
                        setEditSection("Vocal Range");
                        setEditInitialData({
                          vocalRangeID: talentData.vocalRangeID || "",
                        });
                        setEditPromptOpen(true);
                      }}
                      textSx={{ fontSize: "15px" }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}

        {/* DOCUMENTS */}
        {(hasDocumentsData || sidebarMediaEditMode) && (
          <Box mt={2}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
              Documents
            </Typography>

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Passport</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!talentData.hasPassport}
                      disabled={!sidebarMediaEditMode}
                      onChange={(_, v) =>
                        sidebarMediaEditMode &&
                        updateDocuments({ hasPassport: v })
                      }
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>Driver’s License</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!talentData.hasDriverLicencse}
                      disabled={!sidebarMediaEditMode}
                      onChange={(_, v) =>
                        sidebarMediaEditMode &&
                        updateDocuments({ hasDriverLicencse: v })
                      }
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </>
  );
};

export default TalentSidebar;
